import { useState, useEffect, useContext } from 'react'
import styles from './styles.module.css'
import Table from '../../../components/Table'
import Button from '../../../components/Button'
import Counter from '../../../components/Counter'
import { StaffRosterContext } from '../../../utils/context'
import { useSelector } from 'react-redux'

const API_TAB = 'total_work_experience'

export default function Experience() {
  const { allForms, trackDeletion } = useContext(StaffRosterContext)
  const { values, setFieldValue } = allForms

  // Данные из API (Redux)
  const { personTabData } = useSelector(state => state.personnel)
  const { experience: apiExperience } = personTabData || {}

  const [selectedRow, setSelectedRow] = useState(null)
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0])

  // Данные из Formik (composite type: { items, summary })
  const experienceData = values.experience || { items: [], summary: {} }
  const rows = experienceData.items || []
  const summary = experienceData.summary || {}

  const bonusYears = summary.bonus || { days: 0, months: 0, years: 0 }
  const experienceStats = {
    total: summary.total || { days: 0, months: 0, years: 0 },
    pedagogical: summary.pedagogical || { days: 0, months: 0, years: 0 },
    current: summary.current || { days: 0, months: 0, years: 0 }
  }

  // Синхронизация API данных с Formik (только при первой загрузке)
  useEffect(() => {
    if (apiExperience && !rows.length) {
      const data = apiExperience
      const newExperienceData = {
        items: data.items || (Array.isArray(data) ? data : []),
        summary: data.summary || {}
      }
      setFieldValue('experience', newExperienceData)
    }
  }, [apiExperience, rows.length, setFieldValue])

  const handleAddRow = () => {
    const newRow = {
      id: `new_${Date.now()}`,
      date_from: '',
      date_to: '',
      organization_name: '',
      basis: '',
      is_pedagogical: false,
      organization_mo: '',
      position: '',
      department: '',
      salary: '',
      pension: ''
    }
    // Сохраняем в Formik
    setFieldValue('experience', {
      ...experienceData,
      items: [...rows, newRow]
    })
  }

  const handleDeleteRow = () => {
    if (selectedRow !== null) {
      const rowToDelete = rows.find(row => (row.id || row.xp_key) === selectedRow)
      if (rowToDelete) {
        const pk = rowToDelete.xp_key || rowToDelete.id || rowToDelete.xp_fm_id
        if (pk && !String(pk).startsWith('new_')) {
          trackDeletion(API_TAB, pk)
        }
      }
      // Обновляем в Formik
      const updatedRows = rows.filter(row => (row.id || row.xp_key) !== selectedRow)
      setFieldValue('experience', {
        ...experienceData,
        items: updatedRows
      })
      setSelectedRow(null)
    }
  }

  const handleRowClick = (id) => {
    setSelectedRow(selectedRow === id ? null : id)
  }

  const handleRowChange = (id, field, value) => {
    const updatedRows = rows.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    )
    setFieldValue('experience', {
      ...experienceData,
      items: updatedRows
    })
  }

  const handleBonusChange = () => (name, value) => {
    setFieldValue('experience', {
      ...experienceData,
      summary: {
        ...summary,
        bonus: { ...bonusYears, [name]: value }
      }
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('ru-RU')
  }

  return (
    <div className={styles.tabWrapper}>
      {/* Таблица */}
      <div className={styles.qualificationTableSection}>
        <Table>
          <thead>
            <tr>
              <th>С</th>
              <th>По</th>
              <th>Название</th>
              <th>Основание</th>
              <th>Пед. стаж</th>
              <th>Организация МО</th>
              <th>Должность</th>
              <th>Структурное подразделение</th>
              <th>Оклад</th>
              <th>Пансион</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => handleRowClick(row.id)}
                  className={selectedRow === row.id ? styles.selectedRow : ''}
                >
                  <td>
                    <input
                      type="date"
                      value={row.dateFrom}
                      onChange={(e) => handleRowChange(row.id, 'dateFrom', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={row.dateTo}
                      onChange={(e) => handleRowChange(row.id, 'dateTo', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => handleRowChange(row.id, 'name', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.basis}
                      onChange={(e) => handleRowChange(row.id, 'basis', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={row.pedExperience}
                      onChange={(e) => handleRowChange(row.id, 'pedExperience', e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.organizationMO}
                      onChange={(e) => handleRowChange(row.id, 'organizationMO', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.position}
                      onChange={(e) => handleRowChange(row.id, 'position', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.department}
                      onChange={(e) => handleRowChange(row.id, 'department', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.salary}
                      onChange={(e) => handleRowChange(row.id, 'salary', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.pension}
                      onChange={(e) => handleRowChange(row.id, 'pension', e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className={styles.emptyTable}>
                  Нет данных о трудовом стаже
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Статистика стажа */}
      <div className={styles.qualificationForm}>
        <div className={styles.experienceStats}>
          <div className={styles.experienceRow}>
            <label>По состоянию на</label>
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
            />
          </div>

          <div className={styles.experienceRow}>
            <label>Общий стаж</label>
            <span>Дней</span>
            <input type="text" value={experienceStats.total.days} readOnly />
            <span>Месяцев</span>
            <input type="text" value={experienceStats.total.months} readOnly />
            <span>Лет</span>
            <input type="text" value={experienceStats.total.years} readOnly />
          </div>

          <div className={styles.experienceRow}>
            <label>Педагогический стаж</label>
            <span>Дней</span>
            <input type="text" value={experienceStats.pedagogical.days} readOnly />
            <span>Месяцев</span>
            <input type="text" value={experienceStats.pedagogical.months} readOnly />
            <span>Лет</span>
            <input type="text" value={experienceStats.pedagogical.years} readOnly />
          </div>

          <div className={styles.experienceRow}>
            <label>Стаж в данном учреждении</label>
            <span>Дней</span>
            <input type="text" value={experienceStats.current.days} readOnly />
            <span>Месяцев</span>
            <input type="text" value={experienceStats.current.months} readOnly />
            <span>Лет</span>
            <input type="text" value={experienceStats.current.years} readOnly />
          </div>

          <div className={styles.experienceBonusLabel}>
            Дающий право на надбавку за выслугу лет
          </div>
          <div className={styles.experienceBonusRow}>
            <Counter
              label="Дней"
              name="days"
              value={bonusYears.days}
              setFieldValue={handleBonusChange}
            />
            <Counter
              label="Месяцев"
              name="months"
              value={bonusYears.months}
              setFieldValue={handleBonusChange}
            />
            <Counter
              label="Лет"
              name="years"
              value={bonusYears.years}
              setFieldValue={handleBonusChange}
            />
          </div>
        </div>

        {/* Кнопки */}
        <div className={styles.inputsRow}>
          <div className={styles.qualificationButtonsInline}>
            <Button onClick={handleAddRow}>+ Добавить строку</Button>
            <Button mode="delete" onClick={handleDeleteRow}>× Удалить строку</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
